import { useState } from "react";
import { useTranslation } from "react-i18next";
import { QRCodeSVG } from "qrcode.react";
import { 
  Check, 
  Star, 
  Zap, 
  Crown, 
  Gem, 
  Shield,
  Wallet,
  Copy,
  ExternalLink,
  AlertCircle,
  CheckCircle,
  Clock,
  User,
  Mail,
  X,
  Gift,
  Sparkles,
  RefreshCw,
  FileText,
  Megaphone,
  Calendar,
  CreditCard,
  Loader2,
  XCircle
} from "lucide-react";
import { toast } from "@/components/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { activateSubscription } from "@/lib/marketing-api";
import { safeLocalStorage } from "@/utils/safeStorage";

// Plan configurations - bilingual
const getPlans = (isRTL) => [
  {
    id: 'free',
    name: isRTL ? 'مجانية' : 'Free',
    icon: Star,
    color: '#71717A',
    monthlyPrice: 0,
    yearlyPrice: 0,
    membersLimit: 100,
    trialDays: 30,
    megaReachAccess: '7_days',
    features: isRTL ? [
      '100 مشترك كحد أقصى',
      'شهر تجريبي مجاني',
      'منصة واحدة (Telegram)',
      'تصدير JSON فقط',
      'دعم بالبريد الإلكتروني',
      '🎁 منصة ميجا ريتشي (أسبوع مجاني)'
    ] : [
      'Up to 100 members',
      'Free trial month',
      'One platform (Telegram)',
      'JSON export only',
      'Email support',
      '🎁 Mega-Reachy platform (Free week)'
    ],
    notIncluded: isRTL 
      ? ['التصنيف الذكي بالـ AI', 'API للتكامل', 'دعم أولوية']
      : ['AI smart classification', 'API integration', 'Priority support']
  },
  {
    id: 'standard',
    name: isRTL ? 'قياسية' : 'Standard',
    icon: Zap,
    color: '#00F0FF',
    monthlyPrice: 24.99,
    yearlyPrice: 200.92,
    membersLimit: 100000,
    freeWeek: true,
    megaReachAccess: 'unlimited',
    features: isRTL ? [
      '100,000 مشترك',
      'أسبوع مجاني عند الاشتراك',
      'جميع المنصات الأربع',
      'تصدير CSV و JSON',
      'التصنيف الذكي بالـ AI',
      'دعم أولوية',
      '🎁 منصة ميجا ريتشي (مجانية)'
    ] : [
      '100,000 members',
      'Free week on subscription',
      'All four platforms',
      'CSV & JSON export',
      'AI smart classification',
      'Priority support',
      '🎁 Mega-Reachy platform (Free)'
    ],
    notIncluded: isRTL 
      ? ['API للتكامل الخارجي', 'مدير حساب مخصص']
      : ['External API integration', 'Dedicated account manager']
  },
  {
    id: 'pro',
    name: isRTL ? 'احترافية' : 'Pro',
    icon: Crown,
    color: '#7000FF',
    monthlyPrice: 49.99,
    yearlyPrice: 401.92,
    membersLimit: 250000,
    freeMonth: true,
    popular: true,
    megaReachAccess: 'unlimited',
    features: isRTL ? [
      '250,000 مشترك',
      'شهر مجاني للاشتراك السنوي',
      'جميع المنصات + أولوية',
      'API كامل للتكامل',
      'تصنيف AI متقدم',
      'دعم 24/7',
      'تقارير متقدمة',
      '🎁 منصة ميجا ريتشي (مجانية)'
    ] : [
      '250,000 members',
      'Free month for annual subscription',
      'All platforms + priority',
      'Full API integration',
      'Advanced AI classification',
      '24/7 support',
      'Advanced reports',
      '🎁 Mega-Reachy platform (Free)'
    ],
    notIncluded: isRTL ? ['خادم مخصص'] : ['Dedicated server']
  },
  {
    id: 'platinum',
    name: isRTL ? 'بلاتينية' : 'Platinum',
    icon: Gem,
    color: '#FF9500',
    monthlyPrice: 99.00,
    yearlyPrice: 795.96,
    membersLimit: 500000,
    freeMonth: true,
    megaReachAccess: 'unlimited',
    features: isRTL ? [
      '500,000 مشترك',
      'شهر مجاني للاشتراك السنوي',
      'جميع مميزات Pro',
      'مدير حساب مخصص',
      'تكاملات مخصصة',
      'تدريب فريق العمل',
      'SLA مضمون 99.9%',
      '🎁 منصة ميجا ريتشي (مجانية)'
    ] : [
      '500,000 members',
      'Free month for annual subscription',
      'All Pro features',
      'Dedicated account manager',
      'Custom integrations',
      'Team training',
      '99.9% SLA guaranteed',
      '🎁 Mega-Reachy platform (Free)'
    ],
    notIncluded: []
  },
  {
    id: 'expert',
    name: isRTL ? 'الخبراء' : 'Expert',
    icon: Shield,
    color: '#FF003C',
    monthlyPrice: 149.99,
    yearlyPrice: 1206.92,
    membersLimit: 1000000,
    freeMonth: true,
    megaReachAccess: 'unlimited',
    features: isRTL ? [
      '1,000,000 مشترك',
      'شهر مجاني للاشتراك السنوي',
      'جميع مميزات Platinum',
      'خادم مخصص',
      'دعم VIP على مدار الساعة',
      'تطوير ميزات خاصة',
      'استشارات تسويقية',
      '🎁 منصة ميجا ريتشي (مجانية)'
    ] : [
      '1,000,000 members',
      'Free month for annual subscription',
      'All Platinum features',
      'Dedicated server',
      '24/7 VIP support',
      'Custom feature development',
      'Marketing consulting',
      '🎁 Mega-Reachy platform (Free)'
    ],
    notIncluded: []
  }
];

// API URL
const API_URL = process.env.REACT_APP_BACKEND_URL;

const PlanCard = ({ plan, isYearly, onSelect, currentPlan, isRTL }) => {
  const Icon = plan.icon;
  const price = isYearly ? plan.yearlyPrice : plan.monthlyPrice;
  const originalYearlyPrice = plan.monthlyPrice * 12;
  const savings = isYearly ? originalYearlyPrice - plan.yearlyPrice : 0;
  const isCurrent = currentPlan === plan.id;
  const isFree = plan.id === 'free';

  return (
    <div 
      data-testid={`plan-${plan.id}`}
      className={`glass rounded-2xl p-6 relative ${
        plan.popular ? 'neon-glow-violet' : ''
      }`}
      style={{
        border: `3px solid ${plan.color}`,
        boxShadow: `0 0 20px ${plan.color}30`
      }}
    >
      {plan.popular && (
        <div className="absolute -top-3 right-1/2 translate-x-1/2 bg-violet-500 text-black text-xs font-bold px-3 py-1 rounded-full">
          {isRTL ? 'الأكثر شعبية' : 'Most Popular'}
        </div>
      )}

      {isCurrent && (
        <div className="absolute -top-3 right-4 bg-green-500 text-black text-xs font-bold px-3 py-1 rounded-full">
          {isRTL ? 'خطتك الحالية' : 'Current Plan'}
        </div>
      )}

      <div className="flex items-center gap-3 mb-4">
        <div 
          className="w-12 h-12 rounded-xl flex items-center justify-center"
          style={{ background: `${plan.color}20` }}
        >
          <Icon size={24} style={{ color: plan.color }} />
        </div>
        <div>
          <h3 className="text-xl font-bold">{plan.name}</h3>
        </div>
      </div>

      <div className="mb-6">
        <div className="flex items-baseline gap-1 flex-wrap">
          <span className="text-3xl font-mono font-bold" style={{ color: plan.color }}>
            ${price.toFixed(2)}
          </span>
          <span className="text-sm text-neutral-500">
            {isYearly ? (isRTL ? '/سنوياً' : '/year') : (isRTL ? '/شهرياً' : '/month')}
          </span>
        </div>
        {isYearly && savings > 0 && (
          <p className="text-sm text-green-400 mt-1">
            {isRTL ? `وفّر $${savings.toFixed(2)} (خصم 33%)` : `Save $${savings.toFixed(2)} (33% off)`}
          </p>
        )}
        {isFree && (
          <p className="text-sm text-neutral-400 mt-1">
            {isRTL ? 'شهر تجريبي أو 100 مشترك' : 'Trial month or 100 members'}
          </p>
        )}
      </div>

      <div className="bg-black/30 rounded-lg p-3 mb-4">
        <p className="text-sm text-neutral-400">{isRTL ? 'الحد الأقصى للأعضاء' : 'Max Members'}</p>
        <p className="text-2xl font-mono font-bold" style={{ color: plan.color }}>
          {plan.membersLimit.toLocaleString()}
        </p>
      </div>

      {/* MegaReach Access Badge */}
      <div className={`rounded-lg p-3 mb-4 ${
        plan.megaReachAccess === 'unlimited' 
          ? 'bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border border-emerald-500/40'
          : 'bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/40'
      }`}>
        <div className="flex items-center gap-2">
          <Megaphone size={18} className={plan.megaReachAccess === 'unlimited' ? 'text-emerald-400' : 'text-amber-400'} />
          <div>
            <p className={`text-sm font-bold ${plan.megaReachAccess === 'unlimited' ? 'text-emerald-400' : 'text-amber-400'}`}>
              {isRTL ? 'منصة ميجا ريتشي' : 'Mega-Reachy Platform'}
            </p>
            <p className={`text-xs ${plan.megaReachAccess === 'unlimited' ? 'text-emerald-300' : 'text-amber-300'}`}>
              {plan.megaReachAccess === 'unlimited' 
                ? (isRTL ? '✓ مجانية بالكامل' : '✓ Full Access') 
                : (isRTL ? '⏱ أسبوع مجاني فقط' : '⏱ Free week only')}
            </p>
          </div>
        </div>
      </div>

      {(plan.freeWeek || plan.freeMonth) && (
        <div className={`relative overflow-hidden rounded-lg p-3 mb-4 ${
          plan.freeWeek 
            ? 'bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/40' 
            : 'bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border border-emerald-500/40'
        }`}>
          <div className="absolute top-0 right-0 w-16 h-16 opacity-20">
            <Gift size={64} className={plan.freeWeek ? 'text-amber-400' : 'text-emerald-400'} />
          </div>
          <div className="flex items-center gap-2">
            <Sparkles size={18} className={plan.freeWeek ? 'text-amber-400' : 'text-emerald-400'} />
            <div>
              <p className={`text-sm font-bold ${plan.freeWeek ? 'text-amber-400' : 'text-emerald-400'}`}>
                🎁 {isRTL ? 'عرض خاص!' : 'Special Offer!'}
              </p>
              <p className={`text-xs ${plan.freeWeek ? 'text-amber-300' : 'text-emerald-300'}`}>
                {plan.freeWeek 
                  ? (isRTL ? 'أسبوع مجاني' : 'Free week') 
                  : (isRTL ? 'شهر مجاني' : 'Free month')} {isYearly ? (isRTL ? 'عند الاشتراك السنوي' : 'with annual subscription') : ''}
              </p>
            </div>
          </div>
        </div>
      )}

      <ul className="space-y-3 mb-6">
        {plan.features.map((feature, idx) => (
          <li key={idx} className="flex items-start gap-2 text-sm">
            <Check size={16} className="text-green-400 flex-shrink-0 mt-0.5" />
            <span>{feature}</span>
          </li>
        ))}
        {plan.notIncluded?.map((feature, idx) => (
          <li key={`not-${idx}`} className="flex items-start gap-2 text-sm text-neutral-600">
            <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">✕</span>
            <span className="line-through">{feature}</span>
          </li>
        ))}
      </ul>

      <Button
        data-testid={`select-${plan.id}`}
        className="w-full font-semibold"
        style={isFree 
          ? { 
              background: 'transparent', 
              border: '2px solid #FFFFFF', 
              color: '#FFFFFF' 
            } 
          : { 
              background: plan.color, 
              color: '#000' 
            }
        }
        onClick={() => onSelect(plan, isYearly)}
        disabled={isCurrent}
      >
        {isCurrent 
          ? (isRTL ? 'خطتك الحالية' : 'Current Plan') 
          : (isRTL ? 'اشترك الآن' : 'Subscribe Now')}
      </Button>
    </div>
  );
};

const PaymentModal = ({ plan, isYearly, onClose, onConfirm, user, isRTL }) => {
  const [step, setStep] = useState('select_currency'); // select_currency, payment_details, confirmation
  const [selectedCurrency, setSelectedCurrency] = useState('usdttrc20');
  const [invoice, setInvoice] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [autoRenew, setAutoRenew] = useState(true);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);

  const currencies = [
    { code: 'usdttrc20', name: 'USDT', network: 'TRC20', color: '#22C55E', icon: '💵' },
    { code: 'btc', name: 'Bitcoin', network: 'BTC', color: '#EAB308', icon: '₿' },
    { code: 'eth', name: 'Ethereum', network: 'ETH', color: '#3B82F6', icon: '♦' },
    { code: 'bnbbsc', name: 'BNB', network: 'BSC', color: '#A855F7', icon: '🔶' }
  ];

  if (!plan) return null;

  const price = isYearly ? plan.yearlyPrice : plan.monthlyPrice;
  const startDate = new Date();
  const endDate = new Date();
  
  if (isYearly) {
    endDate.setFullYear(endDate.getFullYear() + 1);
  } else {
    endDate.setMonth(endDate.getMonth() + 1);
  }

  if (plan.freeWeek) {
    endDate.setDate(endDate.getDate() + 7);
  } else if (plan.freeMonth) {
    endDate.setMonth(endDate.getMonth() + 1);
  }

  const formatDate = (date) => {
    return date.toLocaleDateString(isRTL ? 'ar-EG' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const copyAddress = () => {
    if (invoice?.pay_address) {
      navigator.clipboard.writeText(invoice.pay_address);
      setCopied(true);
      toast.success(isRTL ? 'تم نسخ عنوان المحفظة' : 'Wallet address copied');
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const createInvoice = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/payments/create-invoice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${safeLocalStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          user_id: user?.id,
          subscription_plan: plan.id,
          billing_cycle: isYearly ? 'yearly' : 'monthly',
          pay_currency: selectedCurrency,
          auto_renew: autoRenew,
          base_url: window.location.origin
        })
      });

      const data = await response.json();
      
      if (data.success && data.invoice) {
        setInvoice(data.invoice);
        setStep('payment_details');
        // Start checking payment status
        startPaymentStatusCheck(data.invoice.payment_id);
      } else {
        toast.error(data.detail || (isRTL ? 'فشل في إنشاء الفاتورة' : 'Failed to create invoice'));
      }
    } catch (error) {
      console.error('Invoice creation error:', error);
      toast.error(isRTL ? 'حدث خطأ في الاتصال' : 'Connection error');
    } finally {
      setLoading(false);
    }
  };

  const startPaymentStatusCheck = (paymentId) => {
    const checkStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/api/payments/status/${paymentId}`);
        const data = await response.json();
        
        if (data.success && data.payment) {
          setPaymentStatus(data.payment.status);
          
          if (['finished', 'confirmed'].includes(data.payment.status)) {
            setStep('confirmation');
            toast.success(isRTL ? 'تم استلام الدفع بنجاح!' : 'Payment received successfully!');
            onConfirm(plan, isYearly, paymentId, data.payment);
            return;
          }
          
          if (['expired', 'failed'].includes(data.payment.status)) {
            toast.error(isRTL ? 'انتهت صلاحية الفاتورة أو فشل الدفع' : 'Invoice expired or payment failed');
            return;
          }
        }
      } catch (error) {
        console.error('Status check error:', error);
      }
    };

    // Check every 10 seconds
    const intervalId = setInterval(checkStatus, 10000);
    setCheckingPayment(true);

    // Stop after 30 minutes
    setTimeout(() => {
      clearInterval(intervalId);
      setCheckingPayment(false);
    }, 30 * 60 * 1000);
  };

  const handleManualConfirm = async () => {
    if (!invoice) return;
    
    setLoading(true);
    try {
      const response = await activateSubscription({
        plan: plan.id,
        billing_cycle: isYearly ? 'yearly' : 'monthly',
        tx_hash: invoice.payment_id,
        amount: price,
        auto_renew: autoRenew
      });

      const result = response.data;
      
      if (result.success) {
        toast.success(isRTL 
          ? 'تم تفعيل اشتراكك! سيتم التحقق من الدفع.'
          : 'Subscription activated! Payment verification in progress.');
        setStep('confirmation');
        onConfirm(plan, isYearly, invoice.payment_id, result.subscription);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || (isRTL ? 'حدث خطأ' : 'An error occurred');
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const safeLocalStorage = {
    getItem: (key) => {
      try { return localStorage.getItem(key); } catch { return null; }
    }
  };

  const cancelInvoice = () => {
    setInvoice(null);
    setStep('select_currency');
    setCheckingPayment(false);
    setPaymentStatus(null);
    toast.info(isRTL ? 'تم إلغاء الفاتورة' : 'Invoice cancelled');
  };

  return (
    <Dialog open={!!plan} onOpenChange={onClose}>
      <DialogContent 
        className="glass max-w-md max-h-[90vh] overflow-y-auto"
        style={{
          border: '2px solid #9333EA',
          borderRadius: '16px',
          boxShadow: '0 0 25px rgba(147, 51, 234, 0.4)'
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="text-cyan-400" />
            {step === 'select_currency' && (isRTL ? 'اختر العملة' : 'Select Currency')}
            {step === 'payment_details' && (isRTL ? 'تفاصيل الدفع' : 'Payment Details')}
            {step === 'confirmation' && (isRTL ? 'تم الدفع!' : 'Payment Complete!')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* User Information */}
          <div className="bg-violet-500/10 border border-violet-500/20 rounded-lg p-4">
            <h4 className="font-semibold text-violet-400 mb-3 flex items-center gap-2">
              <User size={16} />
              {isRTL ? 'بيانات المشترك' : 'Subscriber Information'}
            </h4>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-violet-500/20 flex items-center justify-center">
                  <User size={14} className="text-violet-400" />
                </div>
                <div>
                  <p className="text-xs text-neutral-500">{isRTL ? 'اسم المستخدم' : 'Username'}</p>
                  <p className="font-semibold" data-testid="payment-username">
                    {user?.name || (isRTL ? 'مستخدم' : 'User')}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-violet-500/20 flex items-center justify-center">
                  <Mail size={14} className="text-violet-400" />
                </div>
                <div>
                  <p className="text-xs text-neutral-500">{isRTL ? 'البريد الإلكتروني' : 'Email'}</p>
                  <p className="font-semibold" data-testid="payment-email" dir="ltr">
                    {user?.email || (isRTL ? 'غير متوفر' : 'Not available')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Plan Summary */}
          <div className="bg-black/30 rounded-lg p-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-neutral-400">{isRTL ? 'الخطة' : 'Plan'}</span>
              <span className="font-bold">{plan.name}</span>
            </div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-neutral-400">{isRTL ? 'المدة' : 'Duration'}</span>
              <span>{isYearly ? (isRTL ? 'سنوية' : 'Yearly') : (isRTL ? 'شهرية' : 'Monthly')}</span>
            </div>
            <div className="flex justify-between items-center pt-2 border-t border-white/10">
              <span className="text-neutral-400">{isRTL ? 'المبلغ' : 'Amount'}</span>
              <span className="text-2xl font-mono font-bold text-cyan-400">
                ${price.toFixed(2)}
              </span>
            </div>
          </div>

          {/* Step 1: Currency Selection */}
          {step === 'select_currency' && (
            <>
              {/* Currency Selection */}
              <div>
                <label className="text-sm text-neutral-400 block mb-3">
                  {isRTL ? 'اختر عملة الدفع' : 'Select Payment Currency'}
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {currencies.map((currency) => (
                    <button
                      key={currency.code}
                      onClick={() => setSelectedCurrency(currency.code)}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        selectedCurrency === currency.code
                          ? 'border-cyan-500 bg-cyan-500/10'
                          : 'border-white/10 bg-black/20 hover:border-white/30'
                      }`}
                    >
                      <div className="text-2xl mb-1">{currency.icon}</div>
                      <div className="font-bold" style={{ color: currency.color }}>{currency.name}</div>
                      <div className="text-xs text-neutral-500">{currency.network}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Auto-Renewal Option */}
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 flex-1">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                      <RefreshCw size={20} className="text-blue-400" />
                    </div>
                    <div>
                      <p className="font-bold text-blue-300">{isRTL ? 'التجديد التلقائي' : 'Auto Renewal'}</p>
                      <p className="text-xs text-neutral-400">
                        {isRTL ? 'تجديد الاشتراك تلقائياً' : 'Auto-renew subscription'}
                      </p>
                    </div>
                  </div>
                  <div 
                    className="relative inline-flex h-7 w-14 items-center rounded-full cursor-pointer transition-all flex-shrink-0 overflow-hidden"
                    style={{ 
                      background: autoRenew 
                        ? 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)' 
                        : '#374151',
                      boxShadow: autoRenew 
                        ? '0 0 12px rgba(59, 130, 246, 0.5)' 
                        : '0 0 5px rgba(0,0,0,0.3)'
                    }}
                    onClick={() => setAutoRenew(!autoRenew)}
                    data-testid="auto-renew-toggle"
                  >
                    <span 
                      className="absolute h-5 w-5 rounded-full bg-white shadow-lg transition-all duration-300"
                      style={{ 
                        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                        left: autoRenew ? 'calc(100% - 24px)' : '4px'
                      }}
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Step 2: Payment Details */}
          {step === 'payment_details' && invoice && (
            <>
              {/* Payment Instructions */}
              <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-lg p-4">
                <h4 className="font-semibold text-cyan-400 mb-3 flex items-center gap-2">
                  <AlertCircle size={16} />
                  {isRTL ? 'خطوات الدفع' : 'Payment Steps'}
                </h4>
                <ol className="text-sm text-neutral-400 space-y-2 list-decimal list-inside">
                  <li>{isRTL ? 'افتح محفظتك الرقمية' : 'Open your digital wallet'}</li>
                  <li>
                    {isRTL ? 'أرسل' : 'Send'}{' '}
                    <strong className="text-white">{invoice.pay_amount} {invoice.pay_currency?.toUpperCase()}</strong>
                  </li>
                  <li>{isRTL ? 'انتظر تأكيد الدفع التلقائي' : 'Wait for automatic payment confirmation'}</li>
                </ol>
              </div>

              {/* Payment Address */}
              <div>
                <label className="text-sm text-neutral-400 block mb-2">
                  {isRTL ? 'أرسل إلى هذا العنوان' : 'Send to this address'}
                </label>
                <div className="flex gap-2">
                  <Input
                    value={invoice.pay_address}
                    readOnly
                    className="font-mono text-sm"
                    dir="ltr"
                  />
                  <Button variant="secondary" size="icon" onClick={copyAddress}>
                    {copied ? <CheckCircle size={16} className="text-green-400" /> : <Copy size={16} />}
                  </Button>
                </div>
              </div>

              {/* Amount to Send */}
              <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 text-center">
                <p className="text-sm text-neutral-400 mb-1">{isRTL ? 'المبلغ المطلوب' : 'Amount to Send'}</p>
                <p className="text-3xl font-mono font-bold text-green-400">
                  {invoice.pay_amount} {invoice.pay_currency?.toUpperCase()}
                </p>
                <p className="text-xs text-neutral-500 mt-2">
                  ≈ ${invoice.price_amount_usd?.toFixed(2)} USD
                </p>
              </div>

              {/* Payment Status */}
              {checkingPayment && (
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <Loader2 className="w-5 h-5 text-amber-400 animate-spin" />
                    <div>
                      <p className="font-semibold text-amber-400">
                        {isRTL ? 'في انتظار الدفع...' : 'Waiting for payment...'}
                      </p>
                      <p className="text-xs text-neutral-400">
                        {isRTL ? 'سيتم التأكيد تلقائياً عند استلام الدفع' : 'Will confirm automatically when payment is received'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* QR Code Section */}
              <div className="flex flex-col items-center bg-white rounded-xl p-4">
                <p className="text-sm text-gray-600 mb-3">
                  {isRTL ? 'امسح رمز QR بمحفظتك' : 'Scan QR Code with your wallet'}
                </p>
                <QRCodeSVG 
                  value={invoice.pay_address}
                  size={180}
                  bgColor="#ffffff"
                  fgColor="#000000"
                  level="H"
                  includeMargin={true}
                />
                <p className="text-xs text-gray-500 mt-2 font-mono break-all text-center max-w-[200px]">
                  {invoice.pay_address}
                </p>
              </div>

              {/* Cancel Invoice Button */}
              <div className="pt-2">
                <Button 
                  variant="ghost" 
                  onClick={cancelInvoice}
                  className="w-full text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  data-testid="cancel-invoice-btn"
                >
                  <XCircle size={16} className="mr-2" />
                  {isRTL ? 'إلغاء الفاتورة وإنشاء فاتورة جديدة' : 'Cancel Invoice & Create New'}
                </Button>
              </div>
            </>
          )}

          {/* Step 3: Confirmation */}
          {step === 'confirmation' && (
            <div className="text-center py-6">
              <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={40} className="text-green-400" />
              </div>
              <h3 className="text-xl font-bold text-green-400 mb-2">
                {isRTL ? 'تم الدفع بنجاح!' : 'Payment Successful!'}
              </h3>
              <p className="text-neutral-400 mb-4">
                {isRTL 
                  ? 'تم تفعيل اشتراكك. استمتع بخدماتنا!'
                  : 'Your subscription is now active. Enjoy our services!'}
              </p>
              <div className="bg-white/5 rounded-lg p-4 text-sm">
                <div className="flex justify-between mb-2">
                  <span className="text-neutral-400">{isRTL ? 'الخطة' : 'Plan'}:</span>
                  <span className="font-semibold">{plan.name}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-neutral-400">{isRTL ? 'تاريخ البدء' : 'Start Date'}:</span>
                  <span>{formatDate(startDate)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-400">{isRTL ? 'تاريخ الانتهاء' : 'End Date'}:</span>
                  <span className="text-emerald-400">{formatDate(endDate)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Supported Wallets (only on first two steps) */}
          {step !== 'confirmation' && (
            <div className="text-center">
              <p className="text-xs text-neutral-500 mb-2">{isRTL ? 'المحافظ المدعومة' : 'Supported Wallets'}</p>
              <div className="flex justify-center gap-4 text-xs text-neutral-400">
                <span>Trust Wallet</span>
                <span>Binance</span>
                <span>TronLink</span>
                <span>MetaMask</span>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          {step === 'select_currency' && (
            <>
              <Button variant="ghost" onClick={onClose}>
                {isRTL ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button 
                data-testid="create-invoice-btn"
                onClick={createInvoice}
                disabled={loading}
                className="btn-primary"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 ml-2 animate-spin" />
                    {isRTL ? 'جاري الإنشاء...' : 'Creating...'}
                  </>
                ) : (
                  isRTL ? 'إنشاء فاتورة الدفع' : 'Create Payment Invoice'
                )}
              </Button>
            </>
          )}
          {step === 'payment_details' && (
            <>
              <Button variant="ghost" onClick={() => setStep('select_currency')}>
                {isRTL ? 'رجوع' : 'Back'}
              </Button>
              <Button 
                onClick={handleManualConfirm}
                disabled={loading}
                className="btn-primary"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 ml-2 animate-spin" />
                    {isRTL ? 'جاري التحقق...' : 'Verifying...'}
                  </>
                ) : (
                  isRTL ? 'لقد أرسلت الدفع' : "I've sent the payment"
                )}
              </Button>
            </>
          )}
          {step === 'confirmation' && (
            <Button onClick={onClose} className="btn-primary w-full">
              {isRTL ? 'إغلاق' : 'Close'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Subscription() {
  const [isYearly, setIsYearly] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isYearlyPayment, setIsYearlyPayment] = useState(true);
  const [showComparison, setShowComparison] = useState(false);
  const { user } = useAuth();
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const currentPlan = user?.plan || 'free';
  
  // Get plans based on current language
  const PLANS = getPlans(isRTL);

  const handleSelectPlan = (plan, yearly) => {
    if (plan.id === 'free') {
      toast.success(isRTL ? 'مرحباً بك! بدأت فترتك التجريبية المجانية.' : 'Welcome! Your free trial has started.');
      return;
    }
    setSelectedPlan(plan);
    setIsYearlyPayment(yearly);
  };

  const handlePaymentConfirm = (plan, yearly, txHash, subscriptionData) => {
    console.log('Payment confirmed:', subscriptionData);
    // Here you would send this to your backend for processing
  };

  return (
    <div data-testid="subscription-page" className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="text-center max-w-2xl mx-auto">
        <h1 className="text-4xl font-bold tracking-wide">{isRTL ? 'خطط الاشتراك' : 'Subscription Plans'}</h1>
        <p className="text-neutral-400 mt-3">
          {isRTL ? 'اختر الخطة المناسبة لاحتياجاتك التسويقية' : 'Choose the plan that fits your marketing needs'}
        </p>
      </div>

      {/* Billing Toggle */}
      <div className="flex items-center justify-center gap-4">
        <span className={`text-sm font-semibold transition-colors ${!isYearly ? 'text-white' : 'text-neutral-500'}`}>
          {isRTL ? 'شهري' : 'Monthly'}
        </span>
        <button 
          type="button"
          className="relative h-8 w-16 rounded-full cursor-pointer transition-colors focus:outline-none overflow-hidden"
          style={{ 
            background: isYearly 
              ? 'linear-gradient(135deg, #7C3AED 0%, #A855F7 100%)' 
              : 'linear-gradient(135deg, #00F0FF 0%, #00D4E0 100%)',
            boxShadow: isYearly 
              ? '0 0 15px rgba(124, 58, 237, 0.5)' 
              : '0 0 15px rgba(0, 240, 255, 0.5)'
          }}
          onClick={() => setIsYearly(!isYearly)}
          data-testid="billing-toggle"
          role="switch"
          aria-checked={isYearly}
        >
          <span 
            className="absolute top-1 h-6 w-6 rounded-full bg-white shadow-lg transition-all duration-300 ease-in-out"
            style={{ 
              boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
              left: isYearly ? '36px' : '4px'
            }}
          />
        </button>
        <span className={`text-sm font-semibold transition-colors ${isYearly ? 'text-white' : 'text-neutral-500'}`}>
          {isRTL ? 'سنوي' : 'Yearly'}
        </span>
        {isYearly && (
          <span className="badge badge-success text-xs animate-pulse">{isRTL ? 'وفّر 33%' : 'Save 33%'}</span>
        )}
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
        {PLANS.map(plan => (
          <PlanCard
            key={plan.id}
            plan={plan}
            isYearly={isYearly}
            onSelect={handleSelectPlan}
            currentPlan={currentPlan}
            isRTL={isRTL}
          />
        ))}
      </div>

      {/* Features Comparison Link */}
      <div className="text-center">
        <button 
          onClick={() => setShowComparison(true)}
          className="text-cyan-400 hover:text-cyan-300 text-sm underline cursor-pointer"
        >
          {isRTL ? 'مقارنة تفصيلية بين الخطط' : 'Detailed plan comparison'}
        </button>
      </div>

      {/* Plans Comparison Modal */}
      <Dialog open={showComparison} onOpenChange={setShowComparison}>
        <DialogContent className="glass border-white/10 max-w-5xl max-h-[90vh] overflow-y-auto" dir={isRTL ? 'rtl' : 'ltr'}>
          <DialogHeader>
            <DialogTitle className="text-2xl text-center">{isRTL ? 'مقارنة تفصيلية بين الخطط' : 'Plan Comparison'}</DialogTitle>
          </DialogHeader>
          
          <div className="overflow-x-auto py-4">
            <table className="w-full text-sm" dir={isRTL ? 'rtl' : 'ltr'}>
              <thead>
                <tr className="border-b border-white/10">
                  <th className={`p-3 ${isRTL ? 'text-right' : 'text-left'} text-neutral-400`}>{isRTL ? 'الميزة' : 'Feature'}</th>
                  <th className="p-3 text-center">
                    <div className="flex flex-col items-center">
                      <Star size={20} className="text-neutral-500 mb-1" />
                      <span>{isRTL ? 'مجانية' : 'Free'}</span>
                    </div>
                  </th>
                  <th className="p-3 text-center">
                    <div className="flex flex-col items-center">
                      <Zap size={20} className="text-cyan-400 mb-1" />
                      <span className="text-cyan-400">{isRTL ? 'قياسية' : 'Standard'}</span>
                    </div>
                  </th>
                  <th className="p-3 text-center">
                    <div className="flex flex-col items-center">
                      <Crown size={20} className="text-violet-400 mb-1" />
                      <span className="text-violet-400">{isRTL ? 'احترافية' : 'Professional'}</span>
                    </div>
                  </th>
                  <th className="p-3 text-center">
                    <div className="flex flex-col items-center">
                      <Gem size={20} className="text-orange-400 mb-1" />
                      <span className="text-orange-400">{isRTL ? 'بلاتينية' : 'Platinum'}</span>
                    </div>
                  </th>
                  <th className="p-3 text-center">
                    <div className="flex flex-col items-center">
                      <Shield size={20} className="text-red-400 mb-1" />
                      <span className="text-red-400">{isRTL ? 'الخبراء' : 'Expert'}</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {/* Pricing Row */}
                <tr className="border-b border-white/5 bg-white/5">
                  <td className={`p-3 font-semibold ${isRTL ? 'text-right' : 'text-left'}`}>{isRTL ? 'السعر الشهري' : 'Monthly Price'}</td>
                  <td className="p-3 text-center text-neutral-400">{isRTL ? 'مجاناً' : 'Free'}</td>
                  <td className="p-3 text-center text-cyan-400">$24.99</td>
                  <td className="p-3 text-center text-violet-400">$49.99</td>
                  <td className="p-3 text-center text-orange-400">$99.99</td>
                  <td className="p-3 text-center text-red-400">$149.99</td>
                </tr>
                {/* Members Limit */}
                <tr className="border-b border-white/5">
                  <td className={`p-3 font-semibold ${isRTL ? 'text-right' : 'text-left'}`}>{isRTL ? 'الحد الأقصى للأعضاء' : 'Max Members'}</td>
                  <td className="p-3 text-center">100</td>
                  <td className="p-3 text-center">100,000</td>
                  <td className="p-3 text-center">250,000</td>
                  <td className="p-3 text-center">500,000</td>
                  <td className="p-3 text-center">1,000,000</td>
                </tr>
                {/* MegaReach Access - NEW ROW */}
                <tr className="border-b border-white/5 bg-emerald-500/10">
                  <td className={`p-3 font-semibold ${isRTL ? 'text-right' : 'text-left'} flex items-center gap-2`}>
                    <Megaphone size={16} className="text-emerald-400" />
                    {isRTL ? 'منصة ميجا ريتشي' : 'Mega-Reachy Platform'}
                  </td>
                  <td className="p-3 text-center text-amber-400">{isRTL ? 'أسبوع فقط' : '1 Week Only'}</td>
                  <td className="p-3 text-center text-emerald-400">{isRTL ? '✓ مجانية' : '✓ Free'}</td>
                  <td className="p-3 text-center text-emerald-400">{isRTL ? '✓ مجانية' : '✓ Free'}</td>
                  <td className="p-3 text-center text-emerald-400">{isRTL ? '✓ مجانية' : '✓ Free'}</td>
                  <td className="p-3 text-center text-emerald-400">{isRTL ? '✓ مجانية' : '✓ Free'}</td>
                </tr>
                {/* Free Period */}
                <tr className="border-b border-white/5 bg-white/5">
                  <td className={`p-3 font-semibold ${isRTL ? 'text-right' : 'text-left'}`}>{isRTL ? 'الفترة المجانية' : 'Free Period'}</td>
                  <td className="p-3 text-center">{isRTL ? 'شهر تجريبي' : 'Trial Month'}</td>
                  <td className="p-3 text-center text-amber-400">{isRTL ? 'أسبوع مجاني' : 'Free Week'}</td>
                  <td className="p-3 text-center text-emerald-400">{isRTL ? 'شهر مجاني' : 'Free Month'}</td>
                  <td className="p-3 text-center text-emerald-400">{isRTL ? 'شهر مجاني' : 'Free Month'}</td>
                  <td className="p-3 text-center text-emerald-400">{isRTL ? 'شهر مجاني' : 'Free Month'}</td>
                </tr>
                {/* Auto Renewal - NEW ROW */}
                <tr className="border-b border-white/5">
                  <td className={`p-3 font-semibold ${isRTL ? 'text-right' : 'text-left'} flex items-center gap-2`}>
                    <RefreshCw size={16} className="text-blue-400" />
                    {isRTL ? 'التجديد التلقائي' : 'Auto Renewal'}
                  </td>
                  <td className="p-3 text-center"><X size={16} className="mx-auto text-red-400" /></td>
                  <td className="p-3 text-center"><Check size={16} className="mx-auto text-green-400" /></td>
                  <td className="p-3 text-center"><Check size={16} className="mx-auto text-green-400" /></td>
                  <td className="p-3 text-center"><Check size={16} className="mx-auto text-green-400" /></td>
                  <td className="p-3 text-center"><Check size={16} className="mx-auto text-green-400" /></td>
                </tr>
                {/* Platforms */}
                <tr className="border-b border-white/5 bg-white/5">
                  <td className={`p-3 font-semibold ${isRTL ? 'text-right' : 'text-left'}`}>{isRTL ? 'المنصات المدعومة' : 'Supported Platforms'}</td>
                  <td className="p-3 text-center">{isRTL ? 'Telegram فقط' : 'Telegram Only'}</td>
                  <td className="p-3 text-center">{isRTL ? '4 منصات' : '4 Platforms'}</td>
                  <td className="p-3 text-center">{isRTL ? '4 منصات + أولوية' : '4 Platforms + Priority'}</td>
                  <td className="p-3 text-center">{isRTL ? '4 منصات + أولوية' : '4 Platforms + Priority'}</td>
                  <td className="p-3 text-center">{isRTL ? '4 منصات + أولوية' : '4 Platforms + Priority'}</td>
                </tr>
                {/* Export */}
                <tr className="border-b border-white/5">
                  <td className={`p-3 font-semibold ${isRTL ? 'text-right' : 'text-left'}`}>{isRTL ? 'التصدير' : 'Export'}</td>
                  <td className="p-3 text-center">{isRTL ? 'JSON فقط' : 'JSON Only'}</td>
                  <td className="p-3 text-center">CSV + JSON</td>
                  <td className="p-3 text-center">CSV + JSON</td>
                  <td className="p-3 text-center">CSV + JSON</td>
                  <td className="p-3 text-center">CSV + JSON</td>
                </tr>
                {/* AI Classification */}
                <tr className="border-b border-white/5 bg-white/5">
                  <td className={`p-3 font-semibold ${isRTL ? 'text-right' : 'text-left'}`}>{isRTL ? 'التصنيف الذكي بالـ AI' : 'AI Smart Classification'}</td>
                  <td className="p-3 text-center"><X size={16} className="mx-auto text-red-400" /></td>
                  <td className="p-3 text-center"><Check size={16} className="mx-auto text-green-400" /></td>
                  <td className="p-3 text-center"><Check size={16} className="mx-auto text-green-400" /></td>
                  <td className="p-3 text-center"><Check size={16} className="mx-auto text-green-400" /></td>
                  <td className="p-3 text-center"><Check size={16} className="mx-auto text-green-400" /></td>
                </tr>
                {/* API Access */}
                <tr className="border-b border-white/5">
                  <td className={`p-3 font-semibold ${isRTL ? 'text-right' : 'text-left'}`}>{isRTL ? 'API للتكامل' : 'API Integration'}</td>
                  <td className="p-3 text-center"><X size={16} className="mx-auto text-red-400" /></td>
                  <td className="p-3 text-center"><X size={16} className="mx-auto text-red-400" /></td>
                  <td className="p-3 text-center"><Check size={16} className="mx-auto text-green-400" /></td>
                  <td className="p-3 text-center"><Check size={16} className="mx-auto text-green-400" /></td>
                  <td className="p-3 text-center"><Check size={16} className="mx-auto text-green-400" /></td>
                </tr>
                {/* Support */}
                <tr className="border-b border-white/5 bg-white/5">
                  <td className={`p-3 font-semibold ${isRTL ? 'text-right' : 'text-left'}`}>{isRTL ? 'الدعم الفني' : 'Technical Support'}</td>
                  <td className="p-3 text-center">{isRTL ? 'بريد إلكتروني' : 'Email'}</td>
                  <td className="p-3 text-center">{isRTL ? 'أولوية' : 'Priority'}</td>
                  <td className="p-3 text-center">24/7</td>
                  <td className="p-3 text-center">24/7</td>
                  <td className="p-3 text-center">VIP 24/7</td>
                </tr>
                {/* Account Manager */}
                <tr className="border-b border-white/5">
                  <td className={`p-3 font-semibold ${isRTL ? 'text-right' : 'text-left'}`}>{isRTL ? 'مدير حساب مخصص' : 'Dedicated Account Manager'}</td>
                  <td className="p-3 text-center"><X size={16} className="mx-auto text-red-400" /></td>
                  <td className="p-3 text-center"><X size={16} className="mx-auto text-red-400" /></td>
                  <td className="p-3 text-center"><X size={16} className="mx-auto text-red-400" /></td>
                  <td className="p-3 text-center"><Check size={16} className="mx-auto text-green-400" /></td>
                  <td className="p-3 text-center"><Check size={16} className="mx-auto text-green-400" /></td>
                </tr>
                {/* Dedicated Server */}
                <tr className="border-b border-white/5 bg-white/5">
                  <td className={`p-3 font-semibold ${isRTL ? 'text-right' : 'text-left'}`}>{isRTL ? 'خادم مخصص' : 'Dedicated Server'}</td>
                  <td className="p-3 text-center"><X size={16} className="mx-auto text-red-400" /></td>
                  <td className="p-3 text-center"><X size={16} className="mx-auto text-red-400" /></td>
                  <td className="p-3 text-center"><X size={16} className="mx-auto text-red-400" /></td>
                  <td className="p-3 text-center"><X size={16} className="mx-auto text-red-400" /></td>
                  <td className="p-3 text-center"><Check size={16} className="mx-auto text-green-400" /></td>
                </tr>
              </tbody>
            </table>
          </div>

          <DialogFooter>
            <Button onClick={() => setShowComparison(false)} className="btn-primary">
              {isRTL ? 'إغلاق' : 'Close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Methods Info */}
      <div className="glass rounded-xl p-6 max-w-2xl mx-auto">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Wallet className="text-cyan-400" />
          {isRTL ? 'طرق الدفع المتاحة' : 'Available Payment Methods'}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div 
            className="bg-black/30 rounded-lg p-3 text-center"
            style={{ border: '2px solid #22C55E' }}
          >
            <p className="font-mono font-bold text-green-400">USDT</p>
            <p className="text-xs text-neutral-500">TRC20</p>
          </div>
          <div 
            className="bg-black/30 rounded-lg p-3 text-center"
            style={{ border: '2px solid #EAB308' }}
          >
            <p className="font-mono font-bold text-yellow-400">BTC</p>
            <p className="text-xs text-neutral-500">Bitcoin</p>
          </div>
          <div 
            className="bg-black/30 rounded-lg p-3 text-center"
            style={{ border: '2px solid #3B82F6' }}
          >
            <p className="font-mono font-bold text-blue-400">ETH</p>
            <p className="text-xs text-neutral-500">Ethereum</p>
          </div>
          <div 
            className="bg-black/30 rounded-lg p-3 text-center"
            style={{ border: '2px solid #A855F7' }}
          >
            <p className="font-mono font-bold text-purple-400">BNB</p>
            <p className="text-xs text-neutral-500">BSC</p>
          </div>
        </div>
        <p className="text-xs text-neutral-500 mt-4 text-center">
          {isRTL ? 'العملة المفضلة: USDT على شبكة TRC20 (رسوم أقل)' : 'Preferred currency: USDT on TRC20 network (lower fees)'}
        </p>
      </div>

      {/* Money Back Guarantee */}
      <div className="text-center">
        <div className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/20 rounded-full px-4 py-2">
          <Shield className="text-green-400" size={18} />
          <span className="text-sm text-green-400">{isRTL ? 'ضمان استرداد الأموال خلال 7 أيام' : '7-day money back guarantee'}</span>
        </div>
      </div>

      {/* Payment Modal */}
      <PaymentModal
        plan={selectedPlan}
        isYearly={isYearlyPayment}
        onClose={() => setSelectedPlan(null)}
        onConfirm={handlePaymentConfirm}
        user={user}
        isRTL={isRTL}
      />
    </div>
  );
}
